/** @jsxImportSource theme-ui */
import React, { useMemo } from 'react';
import { Flex, Table, TableColumn, Text } from '@infotrack/zenith-ui';
import type { TopFiveItemType } from '@/api/types/topFiveItemType';
import type {
  ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto,
  ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdExtendedTopFiveItemDto,
} from '@/api/models';
import { getStatusInfo } from '../utils/statusUtils';
import {
  containerStyles,
  tableContainerStyles,
  placeholderStyles,
} from './topFiveItemHistory.styles';

type TopFiveItemHistoryProps = {
  item: TopFiveItemType;
};

type HistoryDataType = {
  id: string;
  event: string;
  old: string;
  new: string;
  date: string;
  user: string;
};

const RELEVANT_COLUMNS = ['Weight', 'Completion', 'Status', 'Result'];

const formatValue = (columnName: string, value: string | null): string => {
  if (value === null || value === undefined) return '';

  if (columnName === 'Status' || columnName === 'Result') {
    const statusInfo = getStatusInfo(
      columnName === 'Status' ? value : null,
      columnName === 'Result' ? value : null
    );
    return statusInfo.text;
  }

  if (columnName === 'Weight' && value) {
    return `${value}%`;
  }

  if (columnName === 'Completion' && value) {
    return `${value}%`;
  }

  return value;
};

const formatDate = (timestamp: string): string => {
  return new Date(timestamp).toLocaleDateString(navigator.language);
};

const shouldFilterOut = (
  audit: ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto
): boolean => {
  const { oldValue, newValue } = audit;

  // Filter out if old and new are the same
  if (oldValue === newValue) return true;

  // Filter out if one is empty string and other is null
  if (
    (oldValue === '' && newValue === null) ||
    (oldValue === null && newValue === '')
  )
    return true;

  // Filter out if numeric values are the same (e.g., "20.00" and "20")
  if (
    oldValue !== null &&
    newValue !== null &&
    oldValue !== undefined &&
    newValue !== undefined
  ) {
    const oldNum = parseFloat(oldValue);
    const newNum = parseFloat(newValue);

    // Check if both are valid numbers and equal
    if (!isNaN(oldNum) && !isNaN(newNum) && oldNum === newNum) {
      return true;
    }
  }

  return false;
};

// Type guard to check if item has auditList (extended version)
const isExtendedTopFiveItem = (
  item: TopFiveItemType
): item is ITopFiveApiV2EndpointsTopFiveGetTopFiveByIdExtendedTopFiveItemDto => {
  return 'auditList' in item;
};

export const TopFiveItemHistory: React.FC<TopFiveItemHistoryProps> = React.memo(
  function TopFiveItemHistory({ item }) {
    const historyData: HistoryDataType[] = useMemo(() => {
      if (
        !isExtendedTopFiveItem(item) ||
        !item.auditList ||
        item.auditList.length === 0
      ) {
        return [];
      }

      const filteredAudits = item.auditList
        .filter(
          (audit: ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto) =>
            audit.columnName &&
            RELEVANT_COLUMNS.includes(audit.columnName) &&
            !shouldFilterOut(audit)
        )
        .sort(
          (
            a: ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto,
            b: ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto
          ) => {
            // Sort by timestamp descending (newest first)
            const dateA = new Date(a.timestamp || '').getTime();
            const dateB = new Date(b.timestamp || '').getTime();
            return dateB - dateA;
          }
        );

      return filteredAudits.map(
        (
          audit: ITopFiveApiV2EndpointsTopFiveSharedModelsAuditDto,
          index: number
        ) => ({
          id: `${audit.auditId || index}`,
          event: audit.columnName || '',
          old: formatValue(audit.columnName || '', audit.oldValue || null),
          new: formatValue(audit.columnName || '', audit.newValue || null),
          date: audit.timestamp ? formatDate(audit.timestamp) : '',
          user: audit.username || '',
        })
      );
    }, [item]);

    const columns = useMemo(
      () =>
        [
          {
            accessorKey: 'event',
            header: 'Event',
          },
          {
            accessorKey: 'old',
            header: 'Old',
          },
          {
            accessorKey: 'new',
            header: 'New',
          },
          {
            accessorKey: 'date',
            header: 'Date',
          },
          {
            accessorKey: 'user',
            header: 'User',
          },
        ] as TableColumn<HistoryDataType>[],
      []
    );

    return (
      <Flex sx={containerStyles}>
        {historyData.length > 0 ? (
          <Flex sx={tableContainerStyles}>
            <Table fullWidth={true} columns={columns} data={historyData} />
          </Flex>
        ) : (
          <Flex sx={placeholderStyles}>
            <Text variant="medium">No history available yet</Text>
          </Flex>
        )}
      </Flex>
    );
  }
);
